import { Component, Story, Knobs, Icons, Accents } from './DocTypes';

const PlaygroundKnobs: Knobs = {
  icon: {
    default: 'Bug',
    editor: 'dropdown',
    editorOptions: Icons,
  },
  accent: {
    default: 'title',
    editor: 'dropdown',
    editorOptions: Accents,
  },
  size: {
    default: 'default',
    editor: 'dropdown',
    editorOptions: ['10', '100', '200', 'default', 'medium', 'large', 'extraLarge'],
  },
  roundedBackground: {
    default: false,
    editor: 'checkbox',
  },
  roundedBackgroundAccent: {
    default: '',
    editor: 'dropdown',
    editorOptions: [''].concat(Accents),
  },
  badge: {
    default: '',
    editor: 'textbox',
  },
  badgeAccent: {
    default: '',
    editor: 'dropdown',
    editorOptions: Accents,
  },
  badgeInline: {
    default: 'false',
    editor: 'checkbox',
  },
  'colour-scheme': {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A tag-icon where you can change all the attributes',
  coreComponent: {
    html: `<tag-icon{{...knobs}}>
</tag-icon>`,
    knobs: {
      ...PlaygroundKnobs,
      badgeStyle: {
        default: '',
        editor: 'multilinetextbox',
      },
    },
  },
  coreComponent_v3: {
    html: `<tag3-icon{{...knobs}}>
</tag3-icon>`,
    knobs: {
      ...PlaygroundKnobs,
      badgeStyle: {
        default: '',
        editor: 'multilinetextbox',
      },
    },
  },
  reactWrapper: {
    jsx: `<TagIcon{{...knobs}} />`,
    knobs: {
      ...PlaygroundKnobs,
      badgeStyle: {
        default: '',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
  reactWrapper_v3: {
    jsx: `<Tag3Icon{{...knobs}} />`,
    knobs: {
      ...PlaygroundKnobs,
      badgeStyle: {
        default: '',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
};

const CustomIconKnobs: Knobs = {
  icon: {
    default:
      'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&h=350',
    editor: 'textbox',
  },
  size: {
    default: '250',
    editor: 'textbox',
  },
};

const CustomIconStory: Story = {
  title: 'Custom Icon',
  description: 'A custom tag-icon',
  coreComponent: {
    html: `<tag-icon {{...knobs}}>
</tag-icon>`,
    knobs: CustomIconKnobs,
  },
  coreComponent_v3: {
    html: `<tag3-icon {{...knobs}}></tag3-icon>`,
    knobs: CustomIconKnobs,
  },
  reactWrapper: { jsx: `<TagIcon {{...knobs}} />`, knobs: CustomIconKnobs },
  reactWrapper_v3: { jsx: `<Tag3Icon {{...knobs}} />`, knobs: CustomIconKnobs },
};

const CustomStyleStory: Story = {
  title: 'Custom Style',
  description: 'A tag-icon with custom style',
  coreComponent: {
    html: `<tag-icon icon='Bug'{{...knobs}}>
</tag-icon>`,
    knobs: {
      iconStyle: {
        default: '{"height": "40px", "width": "40px"}',
        editor: 'multilinetextbox',
      },
    },
  },
  coreComponent_v3: {
    html: `<tag3-icon icon='Bug'{{...knobs}}></tag3-icon>`,
    knobs: {
      iconStyle: {
        default: '{"height": "40px", "width": "40px"}',
        editor: 'multilinetextbox',
      },
    },
  },
  reactWrapper: {
    jsx: `<TagIcon icon='Bug'{{...knobs}} />`,
    knobs: {
      iconStyle: {
        default: '{"height": "40px", "width": "40px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
  reactWrapper_v3: {
    jsx: `<Tag3Icon icon='Bug'{{...knobs}} />`,
    knobs: {
      iconStyle: {
        default: '{"height": "40px", "width": "40px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
};

const badgeKnobs: Knobs = {
  badge: { default: '3', editor: 'textbox' },
  badgeAccent: { default: '', editor: 'dropdown', editorOptions: [''].concat(Accents) },
};
const BadgeStyleStory: Story = {
  title: 'Badge',
  description: 'A tag-icon with custom style',
  coreComponent: {
    html: `<tag-icon icon='Bug'{{...knobs}}>
</tag-icon>`,
    knobs: badgeKnobs,
  },
  coreComponent_v3: {
    html: `<tag3-icon icon='Bug'{{...knobs}}>
</tag3-icon>`,
    knobs: badgeKnobs,
  },
  reactWrapper: { jsx: `<TagIcon icon='Bug'{{...knobs}} />`, knobs: badgeKnobs },
  reactWrapper_v3: { jsx: `<Tag3Icon icon='Bug'{{...knobs}} />`, knobs: badgeKnobs },
};

export const TagIconDocs: Component = {
  type: 'component',
  name: 'icon',
  description: 'Renders an icon',
  mobileFriendly: true,
  popular: false,
  preRelease: '',
  props: [
    {
      name: 'icon',
      description: 'The name of the icon, or an image url reference',
      type: 'Icon | string',
    },
    {
      name: 'accent',
      description: 'The colour of the icon',
      type: 'string',
    },
    {
      name: 'size',
      description: ' The size of the icon',
      type: 'string',
      default: 'default',
    },
    {
      name: 'roundedBackground',
      description: ' The icon has rounded background or not',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'roundedBackgroundAccent',
      description: ' The rounded background accent',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'iconStyle',
      description: ' A CSS JSON string to apply to the internal element',
      type: 'string',
    },
    {
      name: 'badge',
      description: 'The badge value to go on the icon',
      type: 'string',
    },
    {
      name: 'badgeAccent',
      description: 'The badge colour',
      type: 'string',
      default: 'primary',
    },
    {
      name: 'badgeInline',
      description:
        'If set to true the badge is positioned next to the icon, otherwise the badge is displayed top right',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'badgeStyle',
      description: ' A CSS JSON string to apply to the internal element',
      type: 'string',
    },
    {
      name: 'colour-scheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
  ],
  reactPropOverrides: [],
  events: [],
  methods: [],
  stories: [PlaygroundStory, CustomIconStory, CustomStyleStory, BadgeStyleStory],
};
